/**
 * Compliance Calendar — vista unificada de marcos NIS2 + EU AI Act (com Digital Omnibus).
 * Route: /compliance/calendar
 */
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { CalendarClock, Plus, AlertTriangle, ShieldCheck, Building2, BookOpen, Loader2, Trash2, CheckCircle2, Clock, Info, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AnnualComplianceDashboard } from '../components/AnnualComplianceDashboard';

const FRAMEWORK_META = {
  nis2: { label: 'NIS2', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: ShieldCheck },
  ai_act: { label: 'EU AI Act', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: BookOpen },
  cross: { label: 'Cross-reg', tone: 'bg-slate-50 text-slate-700 border-slate-200', icon: Building2 },
  internal: { label: 'Interno', tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: CalendarClock },
};

const SEVERITY_META = {
  critical: { label: 'Crítico', tone: 'bg-red-50 text-red-700 border-red-200' },
  high: { label: 'Alto', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  medium: { label: 'Médio', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Baixo', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  info: { label: 'Info', tone: 'bg-slate-50 text-slate-700 border-slate-200' },
};

function daysLabel(d) {
  if (d < 0) return `${Math.abs(d)} dias em atraso`;
  if (d === 0) return 'Hoje';
  if (d === 1) return 'Amanhã';
  if (d <= 30) return `${d} dias`;
  if (d <= 90) return `${d} dias (~${Math.round(d / 30)} meses)`;
  const years = (d / 365).toFixed(1);
  return `${d} dias (~${years} anos)`;
}

function formatDatePt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

const CATEGORIES_FOR_NEW = [
  { value: 'internal', label: 'Interno (empresa)' },
  { value: 'nis2', label: 'NIS2' },
  { value: 'ai_act', label: 'EU AI Act' },
  { value: 'cross', label: 'Cruz-regulatório' },
];

const SEVERITIES = [
  { value: 'critical', label: 'Crítico' },
  { value: 'high', label: 'Alto' },
  { value: 'medium', label: 'Médio' },
  { value: 'low', label: 'Baixo' },
  { value: 'info', label: 'Informativo' },
];

export default function ComplianceCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterFramework, setFilterFramework] = useState('all');
  const [includePast, setIncludePast] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', date: '', framework: 'internal', severity: 'medium', responsible_email: '', legal_ref: '', recurrence: '' });
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { document.title = 'Calendário de Conformidade · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params = { include_past: includePast };
        if (filterFramework !== 'all') params.framework = filterFramework;
        const [listRes, summaryRes] = await Promise.all([
          api.get('/compliance/calendar', { params }),
          api.get('/compliance/calendar/summary'),
        ]);
        if (ignore) return;
        setItems(Array.isArray(listRes.data?.items) ? listRes.data.items : []);
        setSummary(summaryRes.data);
      } catch (err) {
        if (!ignore) toast.error('Falha ao carregar calendário.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [filterFramework, includePast, refreshKey]);

  const load = () => setRefreshKey(k => k + 1);

  const submit = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error('Título e data são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/compliance/calendar/milestones', {
        ...form,
        recurrence: form.recurrence || null,
      });
      toast.success('Marco adicionado.');
      setShowForm(false);
      setForm({ title: '', description: '', date: '', framework: 'internal', severity: 'medium', responsible_email: '', legal_ref: '', recurrence: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao criar marco.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/compliance/calendar/milestones/${delTarget.id}`);
      toast.success('Marco removido.');
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao remover.');
    }
  };

  const upcoming = useMemo(() => items.filter(i => i.days_until >= 0).slice(0, 20), [items]);
  const overdue = useMemo(() => items.filter(i => i.days_until < 0), [items]);

  const [annualTab, setAnnualTab] = useState('upcoming');

  return (
    <Layout title="Calendário de Conformidade">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="compliance-calendar-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <CalendarClock className="h-6 w-6 text-indigo-600" /> Calendário de Conformidade
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Marcos temporais NIS2 (DL 125/2025) + EU AI Act com calendário revisto pelo <span className="font-medium">Digital Omnibus</span> (jun/2026).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterFramework} onValueChange={setFilterFramework}>
              <SelectTrigger className="w-44" data-testid="calendar-filter-framework">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os frameworks</SelectItem>
                <SelectItem value="nis2">Apenas NIS2</SelectItem>
                <SelectItem value="ai_act">Apenas EU AI Act</SelectItem>
                <SelectItem value="cross">Cruz-regulatório</SelectItem>
                <SelectItem value="internal">Interno</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={includePast ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIncludePast(v => !v)}
              data-testid="calendar-include-past-btn"
            >
              {includePast ? 'A mostrar passados' : 'Ocultar passados'}
            </Button>
            {isAdmin && (
              <Button onClick={() => setShowForm(true)} data-testid="calendar-add-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Marco personalizado
              </Button>
            )}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { key: 'overdue', label: 'Em atraso', tone: 'from-red-500 to-red-600', icon: AlertTriangle, value: summary?.buckets.overdue ?? 0 },
            { key: 'next_30d', label: 'Próximos 30 dias', tone: 'from-orange-500 to-orange-600', icon: Clock, value: summary?.buckets.next_30d ?? 0 },
            { key: 'next_90d', label: 'Próximos 90 dias', tone: 'from-amber-500 to-amber-600', icon: Clock, value: summary?.buckets.next_90d ?? 0 },
            { key: 'critical_upcoming', label: 'Críticos ≤ 90 dias', tone: 'from-indigo-500 to-indigo-600', icon: ShieldCheck, value: summary?.buckets.critical_upcoming ?? 0 },
            { key: 'next_365d', label: 'Próximo ano', tone: 'from-sky-500 to-sky-600', icon: CalendarClock, value: summary?.buckets.next_365d ?? 0 },
          ].map(kpi => (
            <Card key={kpi.key} data-testid={`kpi-${kpi.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{kpi.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${kpi.tone} flex items-center justify-center`}>
                    <kpi.icon className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{kpi.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={annualTab} onValueChange={setAnnualTab}>
          <TabsList data-testid="calendar-tabs">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">Próximos ({upcoming.length})</TabsTrigger>
            <TabsTrigger value="overdue" data-testid="tab-overdue">Em atraso ({overdue.length})</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Todos ({items.length})</TabsTrigger>
            <TabsTrigger value="annual" data-testid="tab-annual">
              <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Painel Cumprimento Anual
            </TabsTrigger>
          </TabsList>

          {['upcoming', 'overdue', 'all'].map(tab => {
            const list = tab === 'upcoming' ? upcoming : tab === 'overdue' ? overdue : items;
            return (
              <TabsContent value={tab} key={tab}>
                <Card>
                  <CardContent className="p-0">
                    {loading ? (
                      <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
                    ) : list.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-sm">Sem marcos neste bucket.</div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {list.map(item => {
                          const fw = FRAMEWORK_META[item.framework] || FRAMEWORK_META.internal;
                          const sev = SEVERITY_META[item.severity] || SEVERITY_META.info;
                          const overdueBadge = item.days_until < 0;
                          return (
                            <div key={item.id} className="p-5 flex gap-5 items-start hover:bg-slate-50/60 transition-colors" data-testid={`milestone-${item.id}`}>
                              <div className="flex-shrink-0 w-28 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                                  {new Date(item.date).toLocaleDateString('pt-PT', { month: 'short' }).toUpperCase()}
                                </div>
                                <div className="text-3xl font-bold text-slate-900 leading-none tabular-nums my-1">
                                  {new Date(item.date).getDate()}
                                </div>
                                <div className="text-xs text-slate-500 tabular-nums">
                                  {new Date(item.date).getFullYear()}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <Badge variant="secondary" className={fw.tone}>
                                    <fw.icon className="h-3 w-3 mr-1" />{fw.label}
                                  </Badge>
                                  <Badge variant="secondary" className={sev.tone}>{sev.label}</Badge>
                                  {item.custom && <Badge variant="secondary" className="bg-slate-100 text-slate-600 border-slate-200">Custom</Badge>}
                                  {item.completed && <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" />Concluído</Badge>}
                                  {overdueBadge && !item.completed && (
                                    <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200">
                                      <AlertTriangle className="h-3 w-3 mr-1" />Em atraso
                                    </Badge>
                                  )}
                                  <span className={`text-xs tabular-nums ${overdueBadge ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                                    · {daysLabel(item.days_until)}
                                  </span>
                                </div>
                                <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
                                {item.description && (
                                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{item.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                  <span>📅 {formatDatePt(item.date)}</span>
                                  {item.legal_ref && <span>⚖️ {item.legal_ref}</span>}
                                  {item.responsible_email && <span>👤 {item.responsible_email}</span>}
                                </div>
                              </div>
                              {isAdmin && item.custom && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => setDelTarget(item)}
                                  data-testid={`milestone-delete-${item.id}`}
                                  title="Remover marco custom"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}

          {/* Annual Compliance Dashboard */}
          <TabsContent value="annual">
            <AnnualComplianceDashboard />
          </TabsContent>
        </Tabs>

        {/* Digital Omnibus banner */}
        <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <Info className="h-5 w-5 text-white" />
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">
              <strong className="text-slate-900">📌 Datas revistas pelo Digital Omnibus (jun/2026):</strong>{' '}
              Este calendário reflete o acordo político final entre Parlamento Europeu (16/jun/2026) e Conselho (29/jun/2026).
              Sistemas de alto risco do <strong>Anexo III</strong> foram adiados de 2/ago/2026 para <strong>2/dez/2027</strong>;
              Anexo I foi adiado de 2/ago/2027 para <strong>2/ago/2028</strong>.
              Obrigações GPAI, literacia em IA (Art. 4) e práticas proibidas (Art. 5) mantêm-se em vigor sem alteração.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Custom milestone dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="calendar-add-dialog">
          <DialogHeader>
            <DialogTitle>Adicionar marco personalizado</DialogTitle>
            <DialogDescription>Marco interno (auditoria, revisão contratual, exercício BCM, etc.).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="calendar-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="calendar-form-date" />
              </div>
              <div>
                <Label>Framework</Label>
                <Select value={form.framework} onValueChange={v => setForm({ ...form, framework: v })}>
                  <SelectTrigger data-testid="calendar-form-framework"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES_FOR_NEW.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Severidade</Label>
                <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="calendar-form-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Responsável (email)</Label>
                <Input value={form.responsible_email} onChange={e => setForm({ ...form, responsible_email: e.target.value })} data-testid="calendar-form-responsible" placeholder="ciso@empresa.pt" />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="calendar-form-description" />
            </div>
            <div>
              <Label>Referência legal (opcional)</Label>
              <Input value={form.legal_ref} onChange={e => setForm({ ...form, legal_ref: e.target.value })} data-testid="calendar-form-legal-ref" placeholder="e.g. NIS2 Art. 21" />
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <Label className="text-xs">Recorrência automática</Label>
              <p className="text-[11px] text-slate-500 mb-2">Ao marcar concluído, gera automaticamente a próxima ocorrência.</p>
              <Select value={form.recurrence || 'none'} onValueChange={v => setForm({ ...form, recurrence: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="calendar-form-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem recorrência</SelectItem>
                  <SelectItem value="annual">Anual</SelectItem>
                  <SelectItem value="biannual">Semestral</SelectItem>
                  <SelectItem value="quarterly">Trimestral</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="calendar-form-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remover marco</DialogTitle>
            <DialogDescription>
              Tem a certeza que quer remover <strong>{delTarget?.title}</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={remove} data-testid="calendar-confirm-delete">Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
